import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
  useEffect,
} from 'react'
import { toast } from 'sonner'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { FROZEN_NODE_EDIT_MESSAGE } from '@/lib/editor/freeze-object-messages'
import {
  canEnterPenPathNodeEdit,
  isShapeFrozenForGeometry,
  isShapeLockedForUserEdit,
} from '@/lib/groups/element-edit-policy'
import { pickPathGeometry } from '../helpers/path-geometry'
import type { CenterCanvasConfig } from '../types/center-canvas-config'

/**
 * Node edit mode's session: entering it (and refusing locked/frozen paths), the geometry-blocked
 * test the overlays and commit wrappers share, and the mode's lifecycle effects — the undo floor,
 * reverting a half-finished drag on exit, and exiting when a shape tool is picked.
 */
export function useNodeEditSession({
  internalShapeManagerRef,
  selectedShape,
  isPenToolActive,
  getUndoStackSize,
  nodeEditShapeId,
  setNodeEditShapeId,
  setNodeEditShapeData,
  nodeEditShapeIdRef,
  nodeEditPreDragShapeRef,
  nodeEditOriginalShapeRef,
  nodeEditUndoFloorRef,
  exitNodeEditMode,
  clearShapeSelectionWithHistory,
  pulseSelectionGeometryBlocked,
  onEnterNodeEditMode,
  onNodeEditModeChange,
}: {
  internalShapeManagerRef: MutableRefObject<ShapeManagerRef | null>
  selectedShape: CenterCanvasConfig['selectedShape']
  isPenToolActive: boolean
  getUndoStackSize: CenterCanvasConfig['getUndoStackSize']
  nodeEditShapeId: string | null
  setNodeEditShapeId: Dispatch<SetStateAction<string | null>>
  setNodeEditShapeData: Dispatch<SetStateAction<ShapeData | null>>
  nodeEditShapeIdRef: MutableRefObject<string | null>
  nodeEditPreDragShapeRef: MutableRefObject<ShapeData | null>
  nodeEditOriginalShapeRef: MutableRefObject<ShapeData | null>
  nodeEditUndoFloorRef: MutableRefObject<number | null>
  exitNodeEditMode: (exitingShapeId: string | undefined) => void
  clearShapeSelectionWithHistory: (afterTextIds?: string[]) => void
  pulseSelectionGeometryBlocked: () => void
  onEnterNodeEditMode: CenterCanvasConfig['onEnterNodeEditMode']
  onNodeEditModeChange: CenterCanvasConfig['onNodeEditModeChange']
}) {
  useEffect(() => {
    nodeEditUndoFloorRef.current = nodeEditShapeId != null ? (getUndoStackSize?.() ?? null) : null
  }, [nodeEditShapeId, getUndoStackSize, nodeEditUndoFloorRef])

  // When node edit mode exits, revert any uncommitted live path changes (e.g. if the
  // user presses Escape during a drag before the pointer-up commit fires). This prevents
  // the shape from retaining expanded/modified bounds from a half-finished drag.
  useEffect(() => {
    const preDrag = nodeEditPreDragShapeRef.current

    nodeEditPreDragShapeRef.current = null

    if (preDrag && nodeEditShapeId === null) {
      const mgr = internalShapeManagerRef.current
      const current = mgr?.getShapes().find((s) => s.id === preDrag.id)

      if (current && current.path !== preDrag.path) {
        mgr?.updateShape(preDrag.id, pickPathGeometry(preDrag))
      }
    }
  }, [nodeEditShapeId, nodeEditPreDragShapeRef, internalShapeManagerRef])

  // Exit node-edit mode when the user picks a shape tool from the panel.
  // selectedShape becomes non-null when any shape button is clicked, which
  // signals intent to draw — not edit an existing path.
  useEffect(() => {
    if (selectedShape != null && nodeEditShapeIdRef.current != null) {
      const exitingShapeId = nodeEditShapeIdRef.current

      exitNodeEditMode(exitingShapeId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShape])

  const tryEnterNodeEditMode = useCallback(
    (shape: ShapeData, opts?: { clearSelectionWhenPen?: boolean }): boolean => {
      const mgr = internalShapeManagerRef.current
      const shapesById = new Map((mgr?.getShapes() ?? []).map((s) => [s.id, s]))

      if (!canEnterPenPathNodeEdit(shape, shapesById)) {
        if (shape.type === 'path' && shape.path && isShapeFrozenForGeometry(shape, shapesById)) {
          pulseSelectionGeometryBlocked()
          toast.error(FROZEN_NODE_EDIT_MESSAGE)
        }

        return false
      }

      setNodeEditShapeId(shape.id)
      setNodeEditShapeData(shape)
      nodeEditOriginalShapeRef.current = shape

      if (opts?.clearSelectionWhenPen && isPenToolActive) {
        clearShapeSelectionWithHistory([])
      }

      onEnterNodeEditMode?.()
      onNodeEditModeChange?.(true)

      return true
    },
    [
      clearShapeSelectionWithHistory,
      isPenToolActive,
      onEnterNodeEditMode,
      onNodeEditModeChange,
      pulseSelectionGeometryBlocked,
      internalShapeManagerRef,
      setNodeEditShapeId,
      setNodeEditShapeData,
      nodeEditOriginalShapeRef,
    ]
  )

  const isNodeEditShapeGeometryBlocked = useCallback(
    (shape: ShapeData): boolean => {
      const mgr = internalShapeManagerRef.current
      const shapesById = new Map((mgr?.getShapes() ?? []).map((s) => [s.id, s]))

      return (
        isShapeLockedForUserEdit(shape, shapesById) || isShapeFrozenForGeometry(shape, shapesById)
      )
    },
    [internalShapeManagerRef]
  )

  return { tryEnterNodeEditMode, isNodeEditShapeGeometryBlocked }
}
